import { useCallback, useEffect, useMemo, useState } from 'react';
import { AiOutlineArrowLeft } from 'react-icons/ai';
import { FiCopy } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button } from '~/components/shadcn/button-simple';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/shadcn/card-simple';
import { Combobox, ComboboxOption } from '~/components/shadcn/combobox-simple';
import { Input } from '~/components/shadcn/input-simple';
import { Label } from '~/components/shadcn/label-simple';
import api from '~/services/api';
import history from '~/services/history';
import copyTextToClipboard from '~/utils/copyTextToClipboard';

import { ItemRouteProps } from '../types';

const EMPTY_FORM = {
  id: null,
  sku: '',
  part_number: '',
  name: '',
  model: '',
  description: '',
  category_id: null,
  brand_id: null,
  manufacturer_id: null,
  subcategory_id: null,
  storage_id: null,
  cpu_id: null,
  ram_id: null,
  operating_system_id: null,
  keyboard_type_id: null,
  screen_size_id: null,
  screen_type_id: null,
  screen_resolution_id: null,
  screen_technology_id: null,
  identifier: '',
  battery_type: [],
  is_active: true,
};

const BATTERY_TYPE_OPTIONS = ['internal', 'removable', 'removable2'];

function requiresBatteryByCategoryName(categoryId, categories) {
  if (categoryId === null || categoryId === undefined) return false;

  const selectedCategory = (categories || []).find((category) => Number(category?.id) === Number(categoryId));
  const categoryName = String(selectedCategory?.name || selectedCategory?.category || '').toLowerCase();

  return categoryName.includes('laptop') || categoryName.includes('tablet');
}

function parseUsersAllowed(rawValue) {
  return String(rawValue || '')
    .split(/[;,]/)
    .map((value) => value.trim())
    .filter(Boolean);
}

function toNumberOrNull(value) {
  if (!value || value === 'null') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

export function ItemManage({ match, location }: ItemRouteProps) {
  const profile = useSelector((state: any) => state.user.profile);
  const itemId = match?.params?.id;
  const isEditMode = Boolean(itemId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isAllowedManage, setIsAllowedManage] = useState(null);

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [storages, setStorages] = useState([]);
  const [cpus, setCpus] = useState([]);
  const [rams, setRams] = useState([]);
  const [operatingSystems, setOperatingSystems] = useState([]);
  const [keyboards, setKeyboards] = useState([]);
  const [screenSizes, setScreenSizes] = useState([]);
  const [screenTypes, setScreenTypes] = useState([]);
  const [screenResolutions, setScreenResolutions] = useState([]);
  const [screenTechnologies, setScreenTechnologies] = useState([]);

  const [formState, setFormState] = useState(EMPTY_FORM);
  const [itemInfo, setItemInfo] = useState(null);

  const [batteryRequired, setBatteryRequired] = useState(false);
  const [identifierList, setIdentifierList] = useState([]);
  const [addingIdentifier, setAddingIdentifier] = useState(false);
  const [newIdentifierDraft, setNewIdentifierDraft] = useState('');

  const searchQuery = useMemo(() => new URLSearchParams(location.search).get('searchQuery') ?? '', [location.search]);

  const categoryOptions = useMemo<ComboboxOption[]>(() => categories.map((item: any) => ({ value: String(item.id), label: item.name })), [categories]);
  const subcategoryOptions = useMemo<ComboboxOption[]>(() => subcategories.map((item: any) => ({ value: String(item.id), label: item.name })), [subcategories]);
  const brandOptions = useMemo<ComboboxOption[]>(() => brands.map((item: any) => ({ value: String(item.id), label: item.name })), [brands]);
  const manufacturerOptions = useMemo<ComboboxOption[]>(
    () => manufacturers.map((item: any) => ({ value: String(item.id), label: item.name })),
    [manufacturers]
  );
  const storageOptions = useMemo<ComboboxOption[]>(() => storages.map((item: any) => ({ value: String(item.id), label: item.name })), [storages]);
  const cpuOptions = useMemo<ComboboxOption[]>(() => cpus.map((item: any) => ({ value: String(item.id), label: item.name })), [cpus]);
  const ramOptions = useMemo<ComboboxOption[]>(() => rams.map((item: any) => ({ value: String(item.id), label: item.name })), [rams]);
  const operatingSystemOptions = useMemo<ComboboxOption[]>(
    () => operatingSystems.map((item: any) => ({ value: String(item.id), label: item.name })),
    [operatingSystems]
  );
  const keyboardOptions = useMemo<ComboboxOption[]>(() => keyboards.map((item: any) => ({ value: String(item.id), label: item.name })), [keyboards]);
  const screenSizeOptions = useMemo<ComboboxOption[]>(() => screenSizes.map((item: any) => ({ value: String(item.id), label: item.name })), [screenSizes]);
  const screenTypeOptions = useMemo<ComboboxOption[]>(() => screenTypes.map((item: any) => ({ value: String(item.id), label: item.name })), [screenTypes]);
  const screenResolutionOptions = useMemo<ComboboxOption[]>(
    () => screenResolutions.map((item: any) => ({ value: String(item.id), label: item.name })),
    [screenResolutions]
  );
  const screenTechnologyOptions = useMemo<ComboboxOption[]>(
    () => screenTechnologies.map((item: any) => ({ value: String(item.id), label: item.name })),
    [screenTechnologies]
  );
  const identifierOptions = useMemo<ComboboxOption[]>(() => identifierList.map((identifier) => ({ value: identifier, label: identifier })), [identifierList]);

  const backToList = searchQuery ? `/items?searchQuery=${encodeURIComponent(searchQuery)}` : '/items';

  const loadIdentifierListByCategory = useCallback(async (categoryId, subcategoryId) => {
    if (categoryId === null) {
      setIdentifierList([]);
      return;
    }

    if ([1, 2, 3, 5].includes(categoryId)) {
      const { data } = await api.get('items/list/identifierBycategory?categoryIds=1, 2, 3, 5');
      setIdentifierList((data || []).map((item) => item.identifier).filter(Boolean));
      return;
    }

    if ([6, 12].includes(categoryId)) {
      const { data } = await api.get('items/list/identifierBycategory?categoryIds=6');
      setIdentifierList((data || []).map((item) => item.identifier).filter(Boolean));
      return;
    }

    if (categoryId === 7 && subcategoryId === 16) {
      const { data } = await api.get('items/list/identifierBycategory?categoryIds=7&subcategoryIds=16');
      setIdentifierList((data || []).map((item) => item.identifier).filter(Boolean));
      return;
    }

    setIdentifierList([]);
  }, []);

  const applyCategoryRules = useCallback(
    async (categoryId, subcategoryId) => {
      setFormState((current) => ({
        ...current,
        keyboard_type_id: null,
        storage_id: null,
        ram_id: null,
        screen_type_id: null,
        cpu_id: null,
      }));

      await loadIdentifierListByCategory(categoryId, subcategoryId);

      if (categoryId !== null && [4, 7, 9, 11, 13, 14, 15].includes(categoryId)) {
        setFormState((current) => ({
          ...current,
          keyboard_type_id: 7,
          storage_id: 256,
          ram_id: 12,
          screen_type_id: 4,
          cpu_id: 93,
        }));
      }

      if (categoryId !== null && [10].includes(categoryId)) {
        setFormState((current) => ({
          ...current,
          keyboard_type_id: 7,
          storage_id: 256,
          ram_id: 12,
          cpu_id: 93,
        }));
      }

      if (categoryId !== null && [6, 12].includes(categoryId)) {
        setFormState((current) => ({
          ...current,
          keyboard_type_id: 7,
          ram_id: 12,
          screen_type_id: 4,
          cpu_id: 93,
        }));
      }

      if (categoryId === 7 && subcategoryId === 16) {
        setFormState((current) => ({
          ...current,
          keyboard_type_id: 7,
          storage_id: 256,
          screen_type_id: 4,
          cpu_id: 93,
        }));
      }

      const requiresBattery = requiresBatteryByCategoryName(categoryId, categories);
      setBatteryRequired(requiresBattery);

      if (!requiresBattery) {
        setFormState((current) => ({ ...current, battery_type: [] }));
      }
    },
    [categories, loadIdentifierListByCategory]
  );

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const [
        categoriesList,
        subcategoriesList,
        brandsList,
        manufacturersList,
        storagesList,
        cpusList,
        ramsList,
        operatingSystemsList,
        keyboardsList,
        screenSizesList,
        screenTypesList,
        screenResolutionsList,
        screenTechnologiesList,
        usersAllowedResponse,
      ] = await Promise.all([
        api.get('categories'),
        api.get('subcategories'),
        api.get('brands'),
        api.get('manufacturers'),
        api.get('storages'),
        api.get('cpus'),
        api.get('rams'),
        api.get('operating_systems'),
        api.get('keyboard_types'),
        api.get('screen_sizes'),
        api.get('screen_types'),
        api.get('screen_resolutions'),
        api.get('screen_technologies'),
        api.get('params?service=Items&key_param=usersAllowed'),
      ]);

      const categoriesData = categoriesList.data || [];

      setCategories(categoriesData);
      setSubcategories(subcategoriesList.data || []);
      setBrands(brandsList.data || []);
      setManufacturers(manufacturersList.data || []);
      setStorages(storagesList.data || []);
      setCpus(cpusList.data || []);
      setRams(ramsList.data || []);
      setOperatingSystems(operatingSystemsList.data || []);
      setKeyboards(keyboardsList.data || []);
      setScreenSizes(screenSizesList.data || []);
      setScreenTypes(screenTypesList.data || []);
      setScreenResolutions(screenResolutionsList.data || []);
      setScreenTechnologies(screenTechnologiesList.data || []);

      const allowedUsers = parseUsersAllowed(usersAllowedResponse.data);
      const allowed = allowedUsers.includes(String(profile?.id));
      setIsAllowedManage(allowed);

      if (!allowed) {
        if (isEditMode && itemId) {
          const queryPart = searchQuery ? `?searchQuery=${encodeURIComponent(searchQuery)}` : '';
          history.replace(`/items/${itemId}/view${queryPart}`);
        } else {
          history.replace('/items');
        }
        return;
      }

      if (!isEditMode || !itemId) {
        setFormState(EMPTY_FORM);
        setBatteryRequired(false);
        setIdentifierList([]);
        setItemInfo(null);
        return;
      }

      const itemResponse = await api.get(`items?id=${itemId}`);
      const loadedItem = Array.isArray(itemResponse.data) ? itemResponse.data[0] : itemResponse.data;

      if (!loadedItem) {
        toast.error('Item not found.');
        history.replace('/items');
        return;
      }

      setItemInfo(loadedItem);

      const nextState = {
        id: loadedItem.id,
        sku: loadedItem.sku ?? '',
        part_number: loadedItem.part_number ?? '',
        name: loadedItem.name ?? '',
        model: loadedItem.model ?? '',
        description: loadedItem.description ?? '',
        category_id: loadedItem.category_id ?? null,
        brand_id: loadedItem.brand_id ?? null,
        manufacturer_id: loadedItem.manufacturer_id ?? null,
        subcategory_id: loadedItem.subcategory_id ?? null,
        storage_id: loadedItem.storage_id ?? null,
        cpu_id: loadedItem.cpu_id ?? null,
        ram_id: loadedItem.ram_id ?? null,
        operating_system_id: loadedItem.operating_system_id ?? null,
        keyboard_type_id: loadedItem.keyboard_type_id ?? null,
        screen_size_id: loadedItem.screen_size_id ?? null,
        screen_type_id: loadedItem.screen_type_id ?? null,
        screen_resolution_id: loadedItem.screen_resolution_id ?? null,
        screen_technology_id: loadedItem.screen_technology_id ?? null,
        identifier: loadedItem.identifier ?? '',
        battery_type: loadedItem?.battery_type
          ? String(loadedItem.battery_type)
              .split(',')
              .map((value) => value.trim())
              .filter(Boolean)
          : [],
        is_active: !(loadedItem.is_active === null || loadedItem.is_active === false),
      };

      setFormState(nextState);
      setBatteryRequired(requiresBatteryByCategoryName(nextState.category_id, categoriesData));

      await loadIdentifierListByCategory(nextState.category_id, nextState.subcategory_id);
    } catch {
      toast.error('Failed to load Item form data.');
      history.replace('/items');
    } finally {
      setLoading(false);
    }
  }, [isEditMode, itemId, loadIdentifierListByCategory, profile?.id, searchQuery]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateField = useCallback((field, value) => {
    setFormState((current) => ({ ...current, [field]: value }));
  }, []);

  const onCategoryChange = useCallback(
    async (nextValue) => {
      const nextCategoryId = toNumberOrNull(nextValue);
      const nextSubcategoryId = formState.subcategory_id;
      setFormState((current) => ({ ...current, category_id: nextCategoryId }));
      await applyCategoryRules(nextCategoryId, nextSubcategoryId);
    },
    [applyCategoryRules, formState.subcategory_id]
  );

  const onSubcategoryChange = useCallback(
    async (nextValue) => {
      const nextSubcategoryId = toNumberOrNull(nextValue);
      const nextCategoryId = formState.category_id;
      setFormState((current) => ({ ...current, subcategory_id: nextSubcategoryId }));
      await applyCategoryRules(nextCategoryId, nextSubcategoryId);
    },
    [applyCategoryRules, formState.category_id]
  );

  const onAddIdentifier = useCallback(() => {
    setAddingIdentifier(true);
    setNewIdentifierDraft(formState.identifier || '');
  }, [formState.identifier]);

  const onCancelAddIdentifier = useCallback(() => {
    setAddingIdentifier(false);
    setNewIdentifierDraft('');
  }, []);

  const onSaveNewIdentifier = useCallback(() => {
    const nextIdentifier = newIdentifierDraft.trim();

    if (!nextIdentifier) {
      toast.error('Identifier cannot be empty.');
      return;
    }

    setIdentifierList((current) => {
      if (current.includes(nextIdentifier)) return current;
      return [...current, nextIdentifier];
    });
    updateField('identifier', nextIdentifier);
    setAddingIdentifier(false);
    setNewIdentifierDraft('');
  }, [newIdentifierDraft, updateField]);

  const onCopyIdentifier = useCallback(async () => {
    const identifierToCopy = formState.identifier?.trim();

    if (!identifierToCopy) {
      toast.error('No identifier selected to copy.');
      return;
    }

    const copied = await copyTextToClipboard(identifierToCopy);

    if (copied) {
      toast.success('Identifier copied');
      return;
    }

    toast.error('Unable to copy identifier');
  }, [formState.identifier]);

  const onSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      const submitter = event?.nativeEvent?.submitter as HTMLButtonElement | undefined;
      if (!submitter || submitter.id !== 'item-manage-save-button') {
        return;
      }

      if (!isAllowedManage) {
        return;
      }

      const requiredMessages = [
        [!formState.sku.trim(), 'SKU is required.'],
        [!formState.part_number.trim(), 'Mfr Part Number is required.'],
        [!formState.name.trim(), 'Name is required.'],
        [!formState.model.trim(), 'Model is required.'],
        [!formState.description.trim(), 'Description is required.'],
        [formState.category_id === null, 'Category is required.'],
        [formState.brand_id === null, 'Brand is required.'],
        [formState.manufacturer_id === null, 'Manufacturer is required.'],
        [formState.storage_id === null, 'Storage is required.'],
        [formState.cpu_id === null, 'CPU is required.'],
        [formState.ram_id === null, 'RAM is required.'],
        [formState.keyboard_type_id === null, 'Keyboard is required.'],
        [formState.screen_type_id === null, 'Screen Type is required.'],
        [identifierList.length > 0 && !formState.identifier.trim(), 'Identifier is required.'],
      ];

      const failed = requiredMessages.find(([invalid]) => invalid);
      if (failed) {
        toast.error(failed[1]);
        return;
      }

      if (batteryRequired && formState.battery_type.length === 0) {
        toast.error('Battery Type is required for Laptop and Tablet categories.');
        return;
      }

      setSaving(true);

      try {
        if (!isEditMode) {
          const itemExists = await api.get(`items?part_number=${encodeURIComponent(formState.part_number.trim())}`);
          if (Array.isArray(itemExists.data) && itemExists.data.length > 0) {
            toast.error('Item Part Number is already registered');
            return;
          }
        }

        const payload: any = {
          part_number: formState.part_number.trim(),
          sku: formState.sku.trim(),
          name: formState.name.trim(),
          model: formState.model.trim(),
          description: formState.description.trim(),
          category_id: formState.category_id,
          identifier: formState.identifier.trim() || '',
          brand_id: formState.brand_id,
          manufacturer_id: formState.manufacturer_id,
          subcategory_id: formState.subcategory_id,
          storage_id: formState.storage_id,
          cpu_id: formState.cpu_id,
          ram_id: formState.ram_id,
          operating_system_id: formState.operating_system_id,
          keyboard_type_id: formState.keyboard_type_id,
          screen_size_id: formState.screen_size_id,
          screen_type_id: formState.screen_type_id,
          screen_resolution_id: formState.screen_resolution_id,
          screen_technology_id: formState.screen_technology_id,
          is_active: formState.is_active,
          battery_type: formState.battery_type.length ? formState.battery_type.join(',') : null,
        };

        if (isEditMode) {
          payload.id = formState.id;

          await api.post('logs', {
            routine: `${itemInfo?.part_number || formState.part_number} Item Updated`,
            action: 'Update',
            previous_data: JSON.stringify(itemInfo),
            new_data: JSON.stringify(payload),
            user_id: profile.id,
            reference_id: payload.id,
          });

          const response = await api.put('items', payload);
          toast.success('Item Updated.');
          history.push(`/items?searchQuery=${encodeURIComponent(response?.data?.part_number || formState.part_number)}`);
          return;
        }

        const response = await api.post('items', payload);

        await api.post('logs', {
          routine: `${response?.data?.part_number || payload.part_number} Item Created`,
          action: 'Create',
          new_data: JSON.stringify(response.data),
          user_id: profile.id,
          reference_id: response?.data?.id,
        });

        toast.success('New Item created.');
        history.push(`/items?searchQuery=${encodeURIComponent(response?.data?.part_number || payload.part_number)}`);
      } catch (error) {
        if (error?.response?.data?.error) {
          toast.error(error.response.data.error);
        } else {
          toast.error('Error saving Item. Contact RBMS administrator.');
        }
      } finally {
        setSaving(false);
      }
    },
    [batteryRequired, formState, identifierList.length, isAllowedManage, isEditMode, itemInfo, profile.id]
  );

  if (loading) {
    return (
      <div style={{ backgroundColor: 'var(--white, #ffffff)', minHeight: '100vh', padding: '75px 20px 20px 250px' }}>
        <div className="space-y-3">
          <div className="h-12 animate-pulse rounded-md bg-muted" />
          <div className="h-28 animate-pulse rounded-md bg-muted" />
          <div className="h-28 animate-pulse rounded-md bg-muted" />
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: 'var(--white, #ffffff)', minHeight: '100vh', padding: '75px 20px 20px 250px', overflow: 'auto' }}>
      <main className="space-y-5">
        <section className="rounded-xl border border-border/80 bg-background/95 p-4 shadow-sm backdrop-blur-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Link to={backToList} className="inline-flex">
              <Button variant="outline" className="gap-2 border-border/70">
                <AiOutlineArrowLeft className="h-4 w-4" />
                Back
              </Button>
            </Link>
            <h1 className="text-xl font-semibold tracking-tight">{isEditMode ? 'Edit Item' : 'New Item'}</h1>
            <div />
          </div>
        </section>

        <form onSubmit={onSubmit} className="space-y-5">
          <Card className="border-border/80 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Item Information</CardTitle>
              <p className="text-xs text-muted-foreground">
                <span className="text-destructive">*</span> Required fields
              </p>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="sku">
                  SKU <span className="text-destructive">*</span>
                </Label>
                <Input id="sku" value={formState.sku} onChange={(event) => updateField('sku', event.target.value)} placeholder="SKU" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="part_number">
                  Mfr Part Number <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="part_number"
                  value={formState.part_number}
                  onChange={(event) => updateField('part_number', event.target.value)}
                  placeholder="Mfr Part Number"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">
                  Name <span className="text-destructive">*</span>
                </Label>
                <Input id="name" value={formState.name} onChange={(event) => updateField('name', event.target.value)} placeholder="Item Name" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="model">
                  Model <span className="text-destructive">*</span>
                </Label>
                <Input id="model" value={formState.model} onChange={(event) => updateField('model', event.target.value)} placeholder="Item Model" required />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="description">
                  Description <span className="text-destructive">*</span>
                </Label>
                <textarea
                  id="description"
                  value={formState.description}
                  onChange={(event) => updateField('description', event.target.value)}
                  rows={4}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  placeholder="Item Description"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="is_active" className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium">
                  <input
                    id="is_active"
                    type="checkbox"
                    checked={formState.is_active}
                    onChange={(event) => updateField('is_active', event.target.checked)}
                    className="h-4 w-4"
                  />
                  Is Active
                </label>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/80 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Item Selection</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="category_id">
                  Category <span className="text-destructive">*</span>
                </Label>
                <Combobox
                  id="category_id"
                  value={String(formState.category_id ?? '')}
                  onChange={onCategoryChange}
                  options={categoryOptions}
                  placeholder="Select category"
                  searchPlaceholder="Search category"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="brand_id">
                  Brand <span className="text-destructive">*</span>
                </Label>
                <Combobox
                  id="brand_id"
                  value={String(formState.brand_id ?? '')}
                  onChange={(value) => updateField('brand_id', toNumberOrNull(value))}
                  options={brandOptions}
                  placeholder="Select brand"
                  searchPlaceholder="Search brand"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="manufacturer_id">
                  Manufacturer <span className="text-destructive">*</span>
                </Label>
                <Combobox
                  id="manufacturer_id"
                  value={String(formState.manufacturer_id ?? '')}
                  onChange={(value) => updateField('manufacturer_id', toNumberOrNull(value))}
                  options={manufacturerOptions}
                  placeholder="Select manufacturer"
                  searchPlaceholder="Search manufacturer"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="subcategory_id">Subcategory</Label>
                <Combobox
                  id="subcategory_id"
                  value={String(formState.subcategory_id ?? '')}
                  onChange={onSubcategoryChange}
                  options={subcategoryOptions}
                  placeholder="Select subcategory"
                  searchPlaceholder="Search subcategory"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/80 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Item Specification</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="storage_id">
                  Storage <span className="text-destructive">*</span>
                </Label>
                <Combobox
                  id="storage_id"
                  value={String(formState.storage_id ?? '')}
                  onChange={(value) => updateField('storage_id', toNumberOrNull(value))}
                  options={storageOptions}
                  placeholder="Select storage"
                  searchPlaceholder="Search storage"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cpu_id">
                  CPU <span className="text-destructive">*</span>
                </Label>
                <Combobox
                  id="cpu_id"
                  value={String(formState.cpu_id ?? '')}
                  onChange={(value) => updateField('cpu_id', toNumberOrNull(value))}
                  options={cpuOptions}
                  placeholder="Select CPU"
                  searchPlaceholder="Search CPU"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ram_id">
                  RAM <span className="text-destructive">*</span>
                </Label>
                <Combobox
                  id="ram_id"
                  value={String(formState.ram_id ?? '')}
                  onChange={(value) => updateField('ram_id', toNumberOrNull(value))}
                  options={ramOptions}
                  placeholder="Select RAM"
                  searchPlaceholder="Search RAM"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="operating_system_id">OS</Label>
                <Combobox
                  id="operating_system_id"
                  value={String(formState.operating_system_id ?? '')}
                  onChange={(value) => updateField('operating_system_id', toNumberOrNull(value))}
                  options={operatingSystemOptions}
                  placeholder="Select operating system"
                  searchPlaceholder="Search operating system"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="keyboard_type_id">
                  Keyboard <span className="text-destructive">*</span>
                </Label>
                <Combobox
                  id="keyboard_type_id"
                  value={String(formState.keyboard_type_id ?? '')}
                  onChange={(value) => updateField('keyboard_type_id', toNumberOrNull(value))}
                  options={keyboardOptions}
                  placeholder="Select keyboard"
                  searchPlaceholder="Search keyboard"
                  required
                />
              </div>

              {identifierList.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="identifier">
                    Identifier <span className="text-destructive">*</span>
                  </Label>
                  {addingIdentifier ? (
                    <div className="space-y-2">
                      <Input
                        id="identifier_new"
                        value={newIdentifierDraft}
                        onChange={(event) => setNewIdentifierDraft(event.target.value)}
                        placeholder="New identifier"
                      />
                      <div className="flex items-center gap-2">
                        <Button type="button" variant="default" size="sm" className="h-10" onClick={onSaveNewIdentifier}>
                          Save
                        </Button>
                        <Button type="button" variant="outline" size="sm" className="h-10" onClick={onCancelAddIdentifier}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <Combobox
                            id="identifier"
                            value={formState.identifier}
                            onChange={(value) => updateField('identifier', value)}
                            options={identifierOptions}
                            placeholder="Select identifier"
                            searchPlaceholder="Search identifier"
                            required
                          />
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-10 w-10"
                          onClick={onCopyIdentifier}
                          aria-label="Copy selected identifier"
                          title="Copy selected identifier"
                        >
                          <FiCopy className="h-4 w-4" />
                        </Button>
                      </div>
                      <Button type="button" variant="outline" size="sm" className="h-10" onClick={onAddIdentifier}>
                        Add new Identifier
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {batteryRequired && (
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="battery_type_internal">
                    Battery Type <span className="text-destructive">*</span>
                  </Label>
                  <div className="flex flex-wrap gap-4 rounded-md border border-border/70 bg-background p-3">
                    {BATTERY_TYPE_OPTIONS.map((option) => {
                      const checked = formState.battery_type.includes(option);

                      return (
                        <label key={option} htmlFor={`battery_type_${option}`} className="inline-flex cursor-pointer items-center gap-2 text-sm">
                          <input
                            id={`battery_type_${option}`}
                            type="checkbox"
                            checked={checked}
                            onChange={(event) => {
                              if (event.target.checked) {
                                updateField('battery_type', [...formState.battery_type, option]);
                              } else {
                                updateField(
                                  'battery_type',
                                  formState.battery_type.filter((value) => value !== option)
                                );
                              }
                            }}
                            className="h-4 w-4"
                          />
                          {option}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-border/80 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Item Specifics: Display</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="screen_size_id">Screen Size</Label>
                <Combobox
                  id="screen_size_id"
                  value={String(formState.screen_size_id ?? '')}
                  onChange={(value) => updateField('screen_size_id', toNumberOrNull(value))}
                  options={screenSizeOptions}
                  placeholder="Select screen size"
                  searchPlaceholder="Search screen size"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="screen_type_id">
                  Screen Type <span className="text-destructive">*</span>
                </Label>
                <Combobox
                  id="screen_type_id"
                  value={String(formState.screen_type_id ?? '')}
                  onChange={(value) => updateField('screen_type_id', toNumberOrNull(value))}
                  options={screenTypeOptions}
                  placeholder="Select screen type"
                  searchPlaceholder="Search screen type"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="screen_resolution_id">Resolution</Label>
                <Combobox
                  id="screen_resolution_id"
                  value={String(formState.screen_resolution_id ?? '')}
                  onChange={(value) => updateField('screen_resolution_id', toNumberOrNull(value))}
                  options={screenResolutionOptions}
                  placeholder="Select resolution"
                  searchPlaceholder="Search resolution"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="screen_technology_id">Technology</Label>
                <Combobox
                  id="screen_technology_id"
                  value={String(formState.screen_technology_id ?? '')}
                  onChange={(value) => updateField('screen_technology_id', toNumberOrNull(value))}
                  options={screenTechnologyOptions}
                  placeholder="Select technology"
                  searchPlaceholder="Search technology"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center justify-end gap-2 pb-6">
            <Button type="button" variant="outline" onClick={() => history.push(backToList)}>
              Cancel
            </Button>
            <Button id="item-manage-save-button" type="submit" variant="default" disabled={saving || isAllowedManage !== true}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
